<script setup lang="ts">
import CancelButton from '@/modules/ui/components/buttons/CancelButton.vue';
import { computed, getCurrentInstance, ref, watch } from 'vue';
import ContinueButton from '@/modules/ui/components/buttons/ContinueButton.vue';
import type { BaseModalProps } from '@/modules/common/types/ui.ts';
import UiModal from '@/modules/ui/components/ui/UiModal.vue';

type ConfirmationPart = { text: string; quotationMarks?: boolean };
const isOpen = defineModel<boolean>('open', { required: true });
const isDirty = defineModel<boolean>('dirty', { default: false });
const {
  confirmationParts,
  title,
  confirmOnDirty = true,
  center,
  width,
  height,
  minHeight,
  minWidth,
  mobileFullHeight = false,
  clipContent = false,
  suspended = false,
} = defineProps<
  BaseModalProps & { confirmationParts?: ConfirmationPart[]; confirmOnDirty?: boolean; suspended?: boolean }
>();
const emit = defineEmits<{ show: []; hide: []; submit: [] }>();
const instance = getCurrentInstance();
const isConfirmationOpen = ref(false);
const normalizedConfirmationParts = computed((): ConfirmationPart[] => {
  if (confirmationParts) return confirmationParts;
  const parts: (ConfirmationPart | undefined)[] = [
    { text: 'ui.modal.common.confirmation.closePrefix' },
    title ? { text: title, quotationMarks: true } : undefined,
    { text: 'ui.modal.common.confirmation.changesLost' },
    { text: 'ui.modal.common.confirmation.continueQuestion' },
  ];
  return parts.filter((part): part is ConfirmationPart => Boolean(part));
});

function onSubmit() {
  emit('submit');
}

const submitListeners = instance?.vnode.props?.onSubmit ? { submit: onSubmit } : {};

function confirm(value: boolean) {
  if (!value && confirmOnDirty && isDirty.value) {
    isOpen.value = true;
    isConfirmationOpen.value = true;
  }
}
watch(isOpen, confirm);
function onOpenUpdate(value: boolean) {
  isOpen.value = value;
  confirm(value);
}
function discard() {
  isDirty.value = false;
  isOpen.value = false;
  isConfirmationOpen.value = false;
}
</script>

<template>
  <UiModal
    :title="title"
    :width="width"
    :height="height"
    :min-height="minHeight"
    :min-width="minWidth"
    :mobile-full-height="mobileFullHeight"
    :clip-content="clipContent"
    :center="center"
    :suspended="suspended"
    :model-value="isOpen || (confirmOnDirty && isDirty)"
    @update:model-value="onOpenUpdate"
    v-on="submitListeners"
    @show="emit('show')"
    @hide="emit('hide')"
  >
    <slot />
    <template
      v-if="$slots.header"
      #header
      ><slot name="header"
    /></template>
    <template
      v-if="$slots.body"
      #body
      ><slot name="body"
    /></template>
    <template
      v-if="$slots.footer"
      #footer
      ><slot name="footer"
    /></template>
    <template
      v-if="$slots.overlay"
      #overlay="slotProps"
      ><slot
        name="overlay"
        v-bind="slotProps"
    /></template>
  </UiModal>
  <slot
    name="confirm"
    :is-confirm-open="isConfirmationOpen"
    :toggle="(value: boolean) => (isConfirmationOpen = value)"
    :discard="discard"
  >
    <UiModal
      v-model="isConfirmationOpen"
      title="ui.modal.common.title.warning"
      popup
    >
      <template #body>
        <p
          v-for="part in normalizedConfirmationParts"
          :key="part.text"
        >
          <template v-if="part.text && part.quotationMarks"> "{{ $t(part.text) }}" </template>
          <template v-else-if="part.text">{{ $t(part.text) }}</template>
        </p>
      </template>
      <template #footer>
        <CancelButton @click="isConfirmationOpen = false" />
        <ContinueButton @click="discard" />
      </template>
    </UiModal>
  </slot>
</template>
